import React, { useState, useEffect, useContext } from 'react';
import Table from '../../components/UI/Table';
import { ProfileContext } from '../../contexts/ProfileContext';
import Dropdown from '../../components/UI/Dropdown';
import ConfirmationModal from '../../components/UI/ConfirmationModal';
import './Members.css';
import apiClient from '../../utils/apiClient';
import membersSearch from '../../assets/icons/devices-search.svg';
import AddMemberModal from './AddMemberModal';
import EditMemberModal from './EditMemberModal';

const ROLE_OPTIONS = ['Admin', 'Member'];
const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '');

const Members = () => {
  const { profile, refreshProfile } = useContext(ProfileContext);

  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [roleDropdownIndex, setRoleDropdownIndex] = useState(null);

  const [pagesData, setPagesData] = useState({});
  const [pageCounts, setPageCounts] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editMember, setEditMember] = useState(null);

  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [isReloading, setIsReloading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const itemsPerPage = 10;
  const pagesPerFetch = Math.ceil(100 / itemsPerPage);

  useEffect(() => {
    const fetchInitial = async () => {
      try {
        await loadBlock(0, 1);
      } catch {
        setErrorMessage('Failed to load members.');
        setShowErrorModal(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchInitial();
  }, []);

  useEffect(() => {
    const reloadData = async () => {
      try {
        await loadBlock(0, 1);
      } catch {
        setErrorMessage('Failed to reload members.');
        setShowErrorModal(true);
      } finally {
        setIsReloading(false);
      }
    };
    if (isReloading) reloadData();
  }, [isReloading]);

  const loadBlock = async (blockIndex, desiredPage) => {
    if (pageCounts[blockIndex] && pagesData[desiredPage]) {
      setCurrentPage(desiredPage);
      return;
    }
    try {
      let resp;
      if (blockIndex === 0) {
        resp = await apiClient.get('/members');
      } else {
        const prevToken = tokens[blockIndex - 1];
        if (!prevToken) return;
        resp = await apiClient.get('/members', { params: { nextToken: prevToken } });
      }

      const arr = Array.isArray(resp.data) ? resp.data : resp.data.members;
      const nextToken = resp.data.nextToken || null;

      const pagesCountInBlock = Math.ceil(arr.length / itemsPerPage);
      const newPagesData = { ...pagesData };
      for (let i = 0; i < pagesCountInBlock; i++) {
        const pageNumber = blockIndex * pagesPerFetch + i + 1;
        const startIdx = i * itemsPerPage;
        newPagesData[pageNumber] = arr.slice(startIdx, startIdx + itemsPerPage);
      }

      const newPageCounts = [...pageCounts];
      newPageCounts[blockIndex] = pagesCountInBlock;

      const newTokens = [...tokens];
      newTokens[blockIndex] = nextToken;

      setPagesData(newPagesData);
      setPageCounts(newPageCounts);
      setTokens(newTokens);
      setCurrentPage(desiredPage);
    } catch (err) {
      console.error(err);
    }
  };

  const handlePageChange = (page) => {
    const blockIndex = Math.floor((page - 1) / pagesPerFetch);
    if (pageCounts[blockIndex]) {
      setCurrentPage(page);
    } else {
      loadBlock(blockIndex, page);
    }
    setOpenDropdownIndex(null);
    setRoleDropdownIndex(null);
  };

  const handleNextBlock = () => {
    const currentBlock = Math.floor((currentPage - 1) / pagesPerFetch);
    const nextBlock = currentBlock + 1;
    if (pageCounts[nextBlock]) {
      setCurrentPage(nextBlock * pagesPerFetch + 1);
    } else {
      loadBlock(nextBlock, nextBlock * pagesPerFetch + 1);
    }
  };

  const handlePrevBlock = () => {
    const currentBlock = Math.floor((currentPage - 1) / pagesPerFetch);
    const prevBlock = currentBlock - 1;
    setCurrentPage(prevBlock * pagesPerFetch + 1);
  };

  const handleDropdownToggle = (index) => setOpenDropdownIndex(openDropdownIndex === index ? null : index);
  const handleRoleToggle = (index) => setRoleDropdownIndex(roleDropdownIndex === index ? null : index);

  const handleExportMembers = async () => {
    try {
      const response = await apiClient.get('/export_members', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'members.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      setErrorMessage('Failed to export members.');
      setShowErrorModal(true);
    }
  };

  const handleRoleChange = async (index, newRole) => {
    const pageMembers = pagesData[currentPage] || [];
    const original = [...pageMembers];
    const lowerRole = newRole.toLowerCase();
    const target = { ...original[index], Roles: lowerRole };
    const memberId = target.UserId;

    setPagesData({
      ...pagesData,
      [currentPage]: original.map((m, i) => (i === index ? target : m)),
    });
    setRoleDropdownIndex(null);

    try {
      const { data: full } = await apiClient.get(`/members/${memberId}`);
      await apiClient.put(`/members/${memberId}`, { ...full, Roles: lowerRole });
    } catch {
      setPagesData({ ...pagesData, [currentPage]: original });
    }
  };

  const handleEditClick = (member) => {
    setRoleDropdownIndex(null);
    setOpenDropdownIndex(null);
    setEditMember(member);
    setShowEditModal(true);
  };

  const handleSaveMember = async (original, newName, newPhone, newRole, newStatus) => {
    const memberId = original.UserId;
    try {
      const { data: full } = await apiClient.get(`/members/${memberId}`);
      await apiClient.put(`/members/${memberId}`, {
        ...full,
        Name: newName,
        Phone: newPhone,
        Roles: newRole,
        Status: newStatus,
      });
      const page = pagesData[currentPage] || [];
      setPagesData({
        ...pagesData,
        [currentPage]: page.map((m) =>
          m.UserId === memberId ? { ...m, Name: newName, Phone: newPhone, Roles: newRole, Status: newStatus } : m,
        ),
      });
      setShowEditModal(false);
      if (memberId === profile?.UserId) await refreshProfile();
    } catch {
      setErrorMessage('Error saving member.');
      setShowErrorModal(true);
    }
  };

  const handleRemoveClick = (member) => {
    setMemberToRemove(member);
    setShowRemoveConfirm(true);
  };

  const handleConfirmRemove = async () => {
    if (!memberToRemove) return;
    try {
      await apiClient.delete(`/members/${memberToRemove.UserId}`);
      setPagesData({});
      setPageCounts([]);
      setTokens([]);
      setIsReloading(true);
    } catch {
      setErrorMessage('Error removing member.');
      setShowErrorModal(true);
    } finally {
      setShowRemoveConfirm(false);
      setMemberToRemove(null);
    }
  };

  const handleAdd = () => {
    setRoleDropdownIndex(null);
    setOpenDropdownIndex(null);
    setShowAddModal(true);
  };

  const handleAddMember = async (name, phone, role, status) => {
    try {
      await apiClient.post('/members', { Name: name, Phone: phone, Roles: role, UserName: phone, Status: status });
      setPagesData({});
      setPageCounts([]);
      setTokens([]);
      setIsReloading(true);
      setShowAddModal(false);
    } catch {
      setErrorMessage('Error adding member.');
      setShowErrorModal(true);
    }
  };

  /* ----------------------------- Table data --------------------------- */
  const currentMembers = pagesData[currentPage] || [];
  const filteredMembers = currentMembers.filter((m) =>
    (m.Name || '').toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const columns = ['MEMBER', 'ROLE', ''];

  const tableData = filteredMembers.map((member, index) => {
    const isLastRow = index === filteredMembers.length - 1;
    return {
      MEMBER: (
        <div>
          <div>{member.Name}</div>
          <div style={{ fontSize: '0.8em', color: '#666' }}>Phone Number: {member.Phone}</div>
        </div>
      ),
      ROLE: (
        <div className="role-dropdown-container">
          <div
            className="role-display"
            onClick={(e) => {
              e.stopPropagation();
              handleRoleToggle(index);
            }}
          >
            {capitalize(member.Roles)}
            <span className="dropdown-arrow">▼</span>
          </div>
          {roleDropdownIndex === index && (
            <div className={`custom-role-dropdown ${isLastRow ? 'open-up' : ''}`}>
              {ROLE_OPTIONS.map((opt) => (
                <div
                  key={opt}
                  className="custom-role-dropdown-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRoleChange(index, opt);
                  }}
                >
                  {opt}
                </div>
              ))}
            </div>
          )}
        </div>
      ),
      '': (
        <div className="actions-container">
          <div
            className="more-button"
            onClick={(e) => {
              e.stopPropagation();
              handleDropdownToggle(index);
            }}
          >
            ⋮
            {openDropdownIndex === index && (
              <Dropdown
                items={[
                  { label: 'Edit', onClick: () => handleEditClick(member) },
                  { label: 'Remove', onClick: () => handleRemoveClick(member), style: { color: 'red' } },
                ]}
                onClose={() => setOpenDropdownIndex(null)}
                placement={isLastRow ? 'top-left' : 'bottom-right'}
              />
            )}
          </div>
        </div>
      ),
    };
  });

  const currentBlockIndex = Math.floor((currentPage - 1) / pagesPerFetch);
  const startPage = currentBlockIndex * pagesPerFetch + 1;
  const pagesInBlock = pageCounts[currentBlockIndex] || 0;

  return (
    <div className="members-page">
      <h1>Member Management</h1>

      <div className="members-action-bar">
        <div className="members-left">
          <div className="members-search">
            <img src={membersSearch} className="members-search-icon" alt="search" />
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
        <div className="members-right">
          <button className="add-button" onClick={handleAdd}>
            + Add
          </button>
          <button className="export-button" onClick={handleExportMembers}>
            Export
          </button>
        </div>
      </div>

      <Table
        columns={columns}
        data={tableData}
        isLoading={isLoading || isReloading}
        placeholder="No members yet"
      />

      <div className="pagination">
        {currentBlockIndex > 0 && <button onClick={handlePrevBlock}>Prev</button>}
        {Array.from({ length: pagesInBlock }, (_, i) => startPage + i).map((page) => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={page === currentPage ? 'active' : ''}
          >
            {page}
          </button>
        ))}
        {tokens[currentBlockIndex] && <button onClick={handleNextBlock}>Next</button>}
      </div>

      {showAddModal && (
        <AddMemberModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddMember}
          roleOptions={ROLE_OPTIONS}
          data-testid="add-member-modal"
        />
      )}

      {showEditModal && editMember && (
        <EditMemberModal
          onClose={() => setShowEditModal(false)}
          onSave={handleSaveMember}
          member={editMember}
          roleOptions={ROLE_OPTIONS}
        />
      )}

      {showRemoveConfirm && (
        <ConfirmationModal
          isOpen={showRemoveConfirm}
          onCancel={() => setShowRemoveConfirm(false)}
          onConfirm={handleConfirmRemove}
          message={`Are you sure you want to remove member "${memberToRemove?.Name}"?`}
          confirmLabel="Remove"
          isDanger={true}
        />
      )}

      {showErrorModal && (
        <ConfirmationModal
          isOpen={showErrorModal}
          onCancel={() => setShowErrorModal(false)}
          onConfirm={() => setShowErrorModal(false)}
          message={errorMessage}
          confirmLabel="OK"
          showCancel={false}
          isDanger={false}
        />
      )}
    </div>
  );
};

export default Members;